package no.vareflow.app.domain

/**
 * Time-limited helpers in the cloud ("Hjelper"): an Ansatt whose membership has `accessUntil`
 * (milliseconds since 1970). Before that moment they work like any Ansatt; after it the server
 * rules refuse every read and write, and the apps log them out with [expiredMessage].
 *
 * A helper logs in with Firmakode + brukernavn + startkode (no e-mail, no own password). The
 * link/QR code only fills in the Firmakode and the brukernavn – the start code is given separately,
 * so a photographed QR code alone does not let anyone in.
 */
object HelperAccess {
    const val HOUR_MS = 3_600_000L
    const val BASE_URL = "https://vareflow.no/"

    /** How long the admin can open a helper's access, in hours. */
    val PRESET_HOURS = listOf(2, 4, 8, 24, 72, 168)
    const val DEFAULT_HOURS = 8

    fun presetLabel(hours: Int): String = when {
        hours < 24 -> t("$hours t", "$hours h")
        hours == 24 -> t("1 dag", "1 day")
        hours % 24 == 0 && hours / 24 == 7 -> t("1 uke", "1 week")
        else -> t("${hours / 24} dager", "${hours / 24} days")
    }

    fun until(now: Long, hours: Int): Long = now + hours * HOUR_MS

    /** An Ansatt with a time limit. Leaders and admins are never time-limited. */
    fun isHelper(user: UserAccount): Boolean = user.role == Role.EMPLOYEE && user.accessUntil != null

    /** No limit, or still before the limit (the same test as firestore.rules). */
    fun isOpen(accessUntil: Long?, now: Long): Boolean = accessUntil == null || now < accessUntil

    fun isExpired(accessUntil: Long?, now: Long): Boolean = !isOpen(accessUntil, now)

    /** Time left for the header pill: "3 t 20 min", "12 min", "45 s" (or "0 s"). */
    fun remainingText(accessUntil: Long, now: Long): String {
        val left = (accessUntil - now).coerceAtLeast(0L)
        val totalMinutes = left / 60_000L
        val days = totalMinutes / (24 * 60)
        val hours = (totalMinutes / 60) % 24
        val minutes = totalMinutes % 60
        return when {
            days > 0 -> t("$days d $hours t", "$days d $hours h")
            hours > 0 -> t("$hours t $minutes min", "$hours h $minutes min")
            totalMinutes > 0 -> "$totalMinutes min"
            else -> "${left / 1000L} s"
        }
    }

    /**
     * The login link: vareflow.no/?firma=SP99&bruker=ola. Opens the login page with Firmakode and
     * brukernavn filled in. Never contains the start code.
     */
    fun loginLink(companyCode: String, username: String, base: String = BASE_URL): String {
        val code = CompanyCode.normalize(companyCode)
        val user = CompanyLogin.normalizeUsername(username)
        return buildString {
            append(base)
            append("?firma=").append(code)
            if (user.isNotEmpty()) append("&bruker=").append(user)
        }
    }

    /** Firmakode and brukernavn from a page address's query ("?firma=SP99&bruker=ola"), or null. */
    fun parseLink(query: String): Pair<String, String>? {
        val params = query.removePrefix("?").split('&').mapNotNull { part ->
            val key = part.substringBefore('=', "").lowercase()
            if (key.isEmpty()) null else key to decode(part.substringAfter('=', ""))
        }.toMap()
        val code = CompanyCode.normalize(params["firma"].orEmpty())
        if (!CompanyCode.isValid(code)) return null
        return code to CompanyLogin.normalizeUsername(params["bruker"].orEmpty())
    }

    private fun decode(value: String): String {
        val bytes = mutableListOf<Byte>()
        var i = 0
        while (i < value.length) {
            val c = value[i]
            when {
                c == '+' -> { bytes += ' '.code.toByte(); i++ }
                c == '%' && i + 2 < value.length -> {
                    val hex = value.substring(i + 1, i + 3).toIntOrNull(16)
                    if (hex != null) { bytes += hex.toByte(); i += 3 } else { bytes += '%'.code.toByte(); i++ }
                }
                else -> { c.toString().encodeToByteArray().forEach { bytes += it }; i++ }
            }
        }
        return bytes.toByteArray().decodeToString()
    }

    val expiredMessage: String
        get() = t("Tilgangen din er utløpt. Be lederen din åpne den igjen.", "Your access has expired. Ask your leader to open it again.")
}
